import { useCallback } from 'react';
import axiosService from 'utils/axios';
import { ErrorResponse } from 'types/general/types';

export interface UseTabOperationsProps {
  vehicleId?: string;
  onError?: (error: ErrorResponse | null) => void;
  setIsLoading?: (loading: boolean) => void;
  onSuccess?: () => void;
}

export const useTabOperations = ({ vehicleId, onError, setIsLoading, onSuccess }: UseTabOperationsProps) => {
  const createTab = useCallback(
    async (name: string, visibility: string[]) => {
      if (!vehicleId) return;

      setIsLoading?.(true);
      try {
        const response = await axiosService.post(`/vehicles/vehicle/${vehicleId}/create_tab/`, {
          name,
          visibility,
        });
        onError?.(null);
        onSuccess?.();
        // Return the created tab data (assuming backend returns it)
        return response.data;
      } catch (err) {
        onError?.(err as ErrorResponse);
      } finally {
        setIsLoading?.(false);
      }
    },
    [vehicleId, setIsLoading, onError, onSuccess]
  );

  const updateTab = useCallback(
    async (tabId: string, name: string, visibility: string[]) => {
      if (!vehicleId) return;

      setIsLoading?.(true);
      try {
        await axiosService.post(`/vehicles/vehicle/${vehicleId}/update_tab/`, {
          // eslint-disable-next-line camelcase
          tab_id: tabId,
          name,
          visibility,
        });
        onError?.(null);
        onSuccess?.();
      } catch (err) {
        onError?.(err as ErrorResponse);
      } finally {
        setIsLoading?.(false);
      }
    },
    [vehicleId, setIsLoading, onError, onSuccess]
  );

  const deleteTab = useCallback(
    async (tabId: string) => {
      if (!vehicleId) return;

      setIsLoading?.(true);
      try {
        await axiosService.post(`/vehicles/vehicle/${vehicleId}/delete_tab/`, {
          // eslint-disable-next-line camelcase
          tab_id: tabId,
        });
        onError?.(null);
        onSuccess?.();
      } catch (err) {
        onError?.(err as ErrorResponse);
      } finally {
        setIsLoading?.(false);
      }
    },
    [vehicleId, setIsLoading, onError, onSuccess]
  );

  return {
    createTab,
    updateTab,
    deleteTab,
  };
};
